import { useMutation, useQueryClient } from '@tanstack/react-query';
import { repoMarginCallService } from '@/services/trades/repo-margin-call-service';
import { useToastStore } from '@/stores/toast-store';
import { formatCurrencyExact } from '@/utils/trades/mtm-calculations';
import { invalidateTradeAndPoolCaches } from './invalidate-trade-caches';
import type { RepoMarginCall, RepoMarginCallRequest } from '@/types';

/**
 * POST /repo-margin-calls/ — post a repo margin call. On success, invalidates the
 * `positions` key tree, which also covers the repo preview/balance and the MTM run
 * log (all keyed under tradesKeys.all), so the bucket readout and history refetch.
 *
 * LR-587: a call is now a POSTING — it writes CashPoolEntry rows — so the pool
 * registry goes stale with it. This is the posting site
 * `invalidateTradeAndPoolCaches` was written for: without it the pool strip on
 * this very screen, and the register's Balance column, keep their five-minute
 * cached figure while the deactivation guard 400s with the balance the screen is
 * not showing.
 */
export const usePostRepoMarginCall = () => {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<RepoMarginCall, Error, RepoMarginCallRequest>({
    mutationFn: (request) => repoMarginCallService.postMarginCall(request),
    onSuccess: (call) => {
      // LR-474: tag the toast figure with a real currency — the hardcoded '$'
      // mislabelled EUR/GBP repo calls. Post-LR-493 that currency is the SETTLEMENT
      // one: net_due/posted_amount are denominated in `settlement_currency` (see
      // RepoMarginCall in mtm.types.ts); `currency` is the trade-lines currency and
      // only equals it on a same-currency book. Same fallback as the ledger's
      // settleCcyOf().
      // LR-479: report what was actually POSTED (posted_amount), which may differ from the
      // computed net_due under a manual override; the direction still follows net_due's sign.
      // Feedback #62: post the figure SIGNED — a Due to CP transfer reads "-$1,000,000.00", not a
      // bare magnitude next to the words "Due to CP". posted_amount shares net_due's sign (LR-479
      // locked) and formatCurrencyExact renders a negative through Intl (which supplies the "-"),
      // so no manual prefix — that would double it.
      const posted = Number(call.posted_amount);
      const amount = formatCurrencyExact(posted, call.settlement_currency || call.currency);
      const label =
        posted === 0
          ? 'no transfer'
          : call.direction === 'due_to_cp'
            ? `Due to CP ${amount}`
            : `Due to us ${amount}`;
      addToast('success', `Repo margin call posted — ${label}`);
      invalidateTradeAndPoolCaches(queryClient);
    },
  });
};
